import React, { useEffect, useId, useRef, useState } from 'react';
import { Button, Icon, Note, Popover } from '@repo/design-system';
import { sameZone } from '@repo/front-end-utils';
import type { TimezoneSource } from '@repo/frontend-types';

import SummaryCard, { SummaryRows, type SummaryRow } from './SummaryCard';
import { TIMEZONE_SOURCE_LABEL } from './twinValueSources';
import { useNowInZone } from './useNowInZone';
import ZonePicker from './ZonePicker';
import { zoneChangeBody, zoneChangeTitle } from './zoneChange';

/**
 * The summary's **Timezone** card (`twin-details-page-summary`, R5; frames 03 and 04; ledger row 29).
 *
 * Opening hours, the launch gate and every daily figure are measured in the twin's zone, so on an
 * existing twin **a chosen zone is not saved straight away**: choosing one other than the stored zone
 * opens a confirm anchored to the picker, saying how far today's figures move, and only *Change
 * Timezone* saves it (`entered`). *Keep*, Escape or a press outside sends nothing. A twin with no zone
 * has nothing to move, so its first zone saves at once, as on the create route.
 *
 * The confirm is the design system's `Popover` with the butter glyph, frame 04's ask anatomy; its
 * wording is undrawn and recorded for Rob (spec Human Action 1).
 */

const NOT_SET = 'Not set';

interface Props {
  /** The twin's stored zone, `''` for none. */
  storedZone: string;
  source: TimezoneSource | null | undefined;
  /** A zone the address points to that differs from the stored one (`useZoneForAddress`). */
  pointsElsewhere: string | null;
  countryCode: string | null;
  countryLabel: string | null;
  /** Save a zone as `entered`. */
  onZone: (zone: string) => void;
}

const SummaryTimezoneCard: React.FC<Props> = ({
  storedZone,
  source,
  pointsElsewhere,
  countryCode,
  countryLabel,
  onZone,
}) => {
  const stored = storedZone.trim();
  const [editing, setEditing] = useState(false);
  /** The zone chosen and waiting on the confirm, or `null` when the confirm is closed. */
  const [pending, setPending] = useState<string | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const changeRef = useRef<HTMLButtonElement>(null);
  const confirmId = useId();
  const now = useNowInZone(stored || null);

  // Focus goes back to the picker when the confirm closes (criterion 11). The Popover returns it
  // to its anchor, which is the picker's wrapper, not its input.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && pending === null) {
      pickerRef.current?.querySelector<HTMLElement>('input')?.focus();
    }
    wasOpen.current = pending !== null;
  }, [pending]);

  const onChoose = (zone: string | null) => {
    if (!zone || (stored && sameZone(zone, stored))) return;
    // Nothing to move on a twin with no zone (R5.5): saved straight away.
    if (!stored) {
      onZone(zone);
      return;
    }
    setPending(zone);
  };

  const keep = () => setPending(null);
  const change = () => {
    if (pending) onZone(pending);
    setPending(null);
  };

  const tag = source ? TIMEZONE_SOURCE_LABEL[source] : undefined;
  const rows: SummaryRow[] = [];
  if (!editing) {
    rows.push({
      key: 'zone',
      label: 'Timezone',
      value: stored || NOT_SET,
      mono: Boolean(stored),
      tag: stored && tag ? { tone: 'peri', text: tag } : undefined,
    });
  }
  // Not a live region: it would announce every minute (criterion 12).
  if (now) rows.push({ key: 'now', label: 'Now there', value: now, mono: true });

  return (
    <SummaryCard
      surface="on-scene"
      title="Timezone"
      glyph="earth-europe"
      discTone="aqua"
      editing={editing}
      onEdit={() => setEditing(true)}
      onDone={() => setEditing(false)}
    >
      {editing && (
        <div
          ref={pickerRef}
          aria-haspopup="dialog"
          aria-expanded={pending !== null}
          aria-controls={pending !== null ? confirmId : undefined}
        >
          <ZonePicker
            countryCode={countryCode}
            countryLabel={countryLabel}
            stored={stored}
            onChoose={onChoose}
          />
        </div>
      )}
      {rows.length > 0 && <SummaryRows rows={rows} />}
      {pointsElsewhere && !editing && (
        <p className="reason">The address points to {pointsElsewhere}.</p>
      )}
      <Note tone="butter" icon={<Icon name="triangle-exclamation" size="xs" />}>
        Changing it on a live twin moves every daily figure. You will be asked first.
      </Note>
      <Popover
        id={confirmId}
        open={pending !== null}
        onClose={keep}
        anchorRef={pickerRef}
        title={pending ? zoneChangeTitle(pending) : ''}
        glyph="triangle-exclamation"
        glyphTone="butter"
        initialFocusRef={changeRef}
        actions={
          <>
            <Button variant="frosted" onClick={keep}>
              Keep {stored}
            </Button>
            <Button ref={changeRef} variant="primary" onClick={change}>
              Change Timezone
            </Button>
          </>
        }
      >
        {pending ? zoneChangeBody(stored, pending) : null}
      </Popover>
    </SummaryCard>
  );
};

export default SummaryTimezoneCard;
